import * as React from "react";
import { IBulkUsageIpAdjustmentIP } from "../../../../types/usageTypes/IBulkUsageIpAdjustmentRequest";

interface IUsageBulkAdjustmentPanelProps {
    selectedGroupCount: number;
    submitting: boolean;
    fromIP: IBulkUsageIpAdjustmentIP | null;
    toIP: IBulkUsageIpAdjustmentIP | null;
    onSearchFromIP: () => void;
    onSearchToIP: () => void;
    onApply: (
        fromIP: IBulkUsageIpAdjustmentIP,
        toIP: IBulkUsageIpAdjustmentIP
    ) => void;
    onCancel: () => void;
}

const UsageBulkAdjustmentPanel: React.FC<IUsageBulkAdjustmentPanelProps> = ({
    selectedGroupCount, submitting, fromIP, toIP, onSearchFromIP, onSearchToIP, onApply, onCancel
}) => {
    const canApply =
        selectedGroupCount > 0 &&
        fromIP !== null &&
        toIP !== null &&
        fromIP.ipiNumber > 0 &&
        toIP.ipiNumber > 0 &&
        Boolean(fromIP.name.trim()) &&
        Boolean(toIP.name.trim()) &&
        fromIP.ipiNumber !== toIP.ipiNumber;
    
    const renderSelectedIP = (
        ip: IBulkUsageIpAdjustmentIP | null,
        side: "From IP" | "To IP"
    ): React.ReactNode => {
        if (!ip) {
            return <p className="bulkAdjustmentPlaceholder">No {side} selected</p>;
        }

        return (
            <div className="bulkAdjustmentIdentity">
                <strong className="bulkAdjustmentIdentityName">{ip.name}</strong>
                <dl className="bulkAdjustmentIdentityFields">
                    <div>
                        <dt>IPI Number</dt>
                        <dd>{ip.ipiNumber}</dd>
                    </div>
                    <div>
                        <dt>Account Number</dt>
                        <dd>{ip.accountNumber || "Not available"}</dd>
                    </div>
                </dl>
            </div>
        );
    };

    return (
        <section className="bulkMatchPanel bulkAdjustmentPanel" aria-label="Search IPs to Adjust">
            <div className="bulkMatchPanelHeader">
                <h3>Search IPs to Adjust</h3>
                <button
                    type="button"
                    className="button btn-defaultSecondary noMargin bulkMatchCancelButton"
                    disabled={submitting}
                    onClick={onCancel}
                >
                    Cancel Adjustment
                </button>
            </div>

            <p className="bulkAdjustmentSelectionCount">
                {`${selectedGroupCount} Usage Group(s) selected`}
            </p>

            <div className="bulkAdjustmentPickers">
                <section className="bulkAdjustmentPicker" aria-label="From IP">
                    <h4>From IP</h4>
                    {fromIP ? (
                        renderSelectedIP(fromIP, "From IP")
                    ) : (
                        <p className="bulkAdjustmentPlaceholder">No IP selected</p>
                    )}
                    <button
                        type="button"
                        className="button btn-defaultPrimary noMargin bulkAdjustmentPickerButton"
                        disabled={submitting}
                        onClick={onSearchFromIP}
                    >
                        {fromIP ? "Change From IP" : "Search From IP"}
                    </button>
                </section>

                <section className="bulkAdjustmentPicker" aria-label="To IP">
                    <h4>To IP</h4>
                    {toIP ? (
                        renderSelectedIP(toIP, "To IP")
                    ) : (
                        <p className="bulkAdjustmentPlaceholder">No IP selected</p>
                    )}
                    <button
                        type="button"
                        className="button btn-defaultPrimary noMargin bulkAdjustmentPickerButton"
                        disabled={submitting}
                        onClick={onSearchToIP}
                    >
                        {toIP ? "Change To IP" : "Search To IP"}
                    </button>
                </section>
            </div>

            {fromIP && toIP && fromIP.ipiNumber === toIP.ipiNumber && (
                <p role="alert" className="bulkAdjustmentError">
                    From IP and To IP must be different.
                </p>
            )}

            <div className="bulkAdjustmentActions">
                <button
                    type="button"
                    className="button btn-defaultPrimary noMargin bulkAdjustmentApplyButton"
                    disabled={!canApply || submitting}
                    onClick={() => {
                        if (canApply && fromIP && toIP) {
                            onApply(fromIP, toIP);
                        }
                    }}
                >
                    Apply
                </button>
            </div>
        </section>
    );
};

export default UsageBulkAdjustmentPanel;